import React, { type ReactNode, type JSX } from 'react';
import { type IStandardButton, type IStandardButtonBody } from '../dm/buttons.js';
import { ChildrenWithOtherContent } from './helpers/ChildrenWithOtherContent.js';
import { SHXIcon } from '../icons/General.js';
import { SHXButton } from './SHXButton.js';
import { useTranslation } from 'react-i18next';
import {
  ArrowClockwise,
  ArrowLeftSquareFill,
  ArrowRightSquareFill,
  ArrowsFullscreen,
  CalendarEventFill,
  CaretDown,
  CaretDownFill,
  CaretLeft,
  CaretLeftFill,
  CaretRight,
  CaretRightFill,
  CaretUp,
  CaretUpFill,
  ChevronContract,
  ChevronExpand,
  Circle,
  CircleFill,
  ClipboardFill,
  CloudDownloadFill,
  Copy,
  Floppy2Fill,
  FullscreenExit,
  Palette,
  PencilFill,
  PinAngle,
  PinFill,
  Star,
  StarFill,
  Search,
  TrashFill,
  XCircleFill,
  InfoCircle,
  Gear,
  GearFill,
  PlusCircle,
  DashCircle,
  WindowStack,
} from 'react-bootstrap-icons';

export * from './SHXButton.js';

interface IHandledDefault extends IStandardButtonBody {
  buttonName: string;
  icon: JSX.Element;
}
const RenderedChildren = ({ showDefaultText, text, buttonName, icon }: IHandledDefault) => {
  const { t } = useTranslation(['uicommon']);

  if (showDefaultText && !text) {
    return (
      <ChildrenWithOtherContent
        text={t(`uicommon:buttons.${buttonName}.btnText`)}
        icon={icon}
        i18nKey={`uicommon:buttons.${buttonName}.withContent`}
      />
    );
  }

  return <ChildrenWithOtherContent text={text} icon={icon} i18nKey={`uicommon:buttons.${buttonName}.withContent`} />;
};

export const StandardButton = ({
  children,
  type,
  variant,
  tooltip,
  itemType,
  done,
  fill,
  showDefaultText = false,
  ...props
}: IStandardButton) => {
  const { t } = useTranslation(['uicommon']);

  const btnProps: IStandardButtonBody = { text: children, done, fill, showDefaultText };

  let renderedChildren: ReactNode | undefined;
  let renderedTooltip: string | undefined = tooltip;
  if (!renderedTooltip) {
    renderedTooltip = t(`uicommon:buttons.${type}.tooltip`, { itemType });
  }

  let renderedVariant: string | undefined = variant;

  switch (type) {
    case 'cancel': {
      renderedChildren = <RenderedChildren buttonName="cancel" icon={<XCircleFill />} {...btnProps} />;
      break;
    }
    case 'delete': {
      renderedChildren = <RenderedChildren buttonName="delete" icon={<TrashFill />} {...btnProps} />;
      if (!renderedVariant) renderedVariant = 'outline-danger';
      break;
    }
    case 'edit': {
      renderedChildren = <RenderedChildren buttonName="edit" icon={<PencilFill />} {...btnProps} />;
      break;
    }
    case 'copyToClipboard': {
      renderedChildren = <RenderedChildren buttonName="copyToClipboard" icon={<ClipboardFill />} {...btnProps} />;
      break;
    }
    case 'colours': {
      renderedChildren = <RenderedChildren buttonName="colours" icon={<Palette />} {...btnProps} />;
      break;
    }
    case 'save': {
      renderedChildren = <RenderedChildren buttonName="save" icon={<Floppy2Fill />} {...btnProps} />;
      break;
    }
    case 'refresh': {
      renderedChildren = <RenderedChildren buttonName="refresh" icon={<ArrowClockwise />} {...btnProps} />;
      break;
    }
    case 'duplicate': {
      renderedChildren = <RenderedChildren buttonName="duplicate" icon={<Copy />} {...btnProps} />;
      break;
    }
    case 'complete': {
      renderedChildren = (
        <RenderedChildren buttonName="complete" icon={done ? <CircleFill /> : <Circle />} {...btnProps} />
      );
      break;
    }
    case 'pin': {
      renderedChildren = <RenderedChildren buttonName="pin" icon={done ? <PinFill /> : <PinAngle />} {...btnProps} />;
      break;
    }
    case 'star': {
      renderedChildren = <RenderedChildren buttonName="star" icon={done ? <StarFill /> : <Star />} {...btnProps} />;
      break;
    }
    case 'openCloseBible': {
      renderedChildren = (
        <RenderedChildren
          buttonName="openCloseBible"
          icon={done ? <SHXIcon type="bibleopen" /> : <SHXIcon type="bibleclosed" />}
          {...btnProps}
        />
      );
      break;
    }
    case 'showHide': {
      renderedChildren = (
        <RenderedChildren buttonName="showHide" icon={done ? <ChevronContract /> : <ChevronExpand />} {...btnProps} />
      );
      break;
    }
    case 'left': {
      renderedChildren = (
        <RenderedChildren buttonName="left" icon={fill ? <CaretLeftFill /> : <CaretLeft />} {...btnProps} />
      );
      if (!renderedVariant) renderedVariant = 'outline-dark';
      break;
    }
    case 'right': {
      renderedChildren = (
        <RenderedChildren buttonName="right" icon={fill ? <CaretRightFill /> : <CaretRight />} {...btnProps} />
      );
      if (!renderedVariant) renderedVariant = 'outline-dark';
      break;
    }
    case 'up': {
      renderedChildren = <RenderedChildren buttonName="up" icon={fill ? <CaretUpFill /> : <CaretUp />} {...btnProps} />;
      if (!renderedVariant) renderedVariant = 'outline-dark';
      break;
    }
    case 'down': {
      renderedChildren = (
        <RenderedChildren buttonName="down" icon={fill ? <CaretDownFill /> : <CaretDown />} {...btnProps} />
      );
      if (!renderedVariant) renderedVariant = 'outline-dark';
      break;
    }
    case 'calendar': {
      renderedChildren = <RenderedChildren buttonName="calendar" icon={<CalendarEventFill />} {...btnProps} />;
      break;
    }
    case 'search': {
      renderedChildren = <RenderedChildren buttonName="search" icon={<Search />} {...btnProps} />;
      break;
    }
    case 'next': {
      renderedChildren = <RenderedChildren buttonName="next" icon={<ArrowRightSquareFill />} {...btnProps} />;
      break;
    }
    case 'prev': {
      renderedChildren = <RenderedChildren buttonName="prev" icon={<ArrowLeftSquareFill />} {...btnProps} />;
      break;
    }
    case 'fullscreen': {
      renderedChildren = (
        <RenderedChildren
          buttonName="fullscreen"
          icon={done ? <FullscreenExit /> : <ArrowsFullscreen />}
          {...btnProps}
        />
      );
      break;
    }
    case 'loadMore': {
      renderedChildren = <RenderedChildren buttonName="loadMore" icon={<CloudDownloadFill />} {...btnProps} />;
      break;
    }
    case 'help': {
      renderedChildren = <RenderedChildren buttonName="help" icon={<InfoCircle />} {...btnProps} />;
      break;
    }
    case 'settings': {
      renderedChildren = <RenderedChildren buttonName="settings" icon={done ? <GearFill /> : <Gear />} {...btnProps} />;
      break;
    }
    case 'add': {
      renderedChildren = <RenderedChildren buttonName="add" icon={<PlusCircle />} {...btnProps} />;
      break;
    }
    case 'remove': {
      renderedChildren = <RenderedChildren buttonName="remove" icon={<DashCircle />} {...btnProps} />;
      break;
    }
    case 'popOut': {
      renderedChildren = <RenderedChildren buttonName="popOut" icon={<WindowStack />} {...btnProps} />;
      break;
    }
    default: {
      return null;
    }
  }

  if (!renderedVariant) renderedVariant = 'outline-secondary';

  return (
    <SHXButton variant={renderedVariant} tooltip={renderedTooltip} {...props}>
      {renderedChildren}
    </SHXButton>
  );
};
